/**
 * Synchronous score computation for PdfStaticModal.
 * Used as lazy useState initializers so values are available in SSR
 * (ReactDOMServer.renderToStaticMarkup) and on first browser render,
 * without waiting for useEffect.
 */

import {
  bacteriaMetaConfig,
  bacteriaMetaConfig_9P,
  bacteriaMetaConfig_allergy,
  bacteriaMetaConfig_allergy_kids,
  bacteriaMetaConfig_toxin,
  bacteriaMetaConfig_oms,
  bacteriaMetaConfig_bwcallergy,
  bacteriaMetaConfig_bwccore,
  bacteriaMetaConfig_bwcdiamond,
} from '../configs/calculate_ctvalue';
import { evaluateBacteriaCondition } from './bacteriaEvaluator';
import {
  vitaminB_sp,
  vitaminB_multi,
  vitaminB_spCondition,
} from '../configs/extraresult/vitaminsBConfig';
import {
  neurotransmitter_sp,
  neurotransmitter_multi,
  neurotransmitter_spCondition,
} from '../configs/extraresult/neurotransmitterConfig';
import {
  metabolites_sp,
  metabolites_multi,
  metabolites_spCondition,
} from '../configs/extraresult/metabolitesConfig';
import {
  macronutrient_sp,
  macronutrient_multi,
  macronutrient_harmfulsp,
  macronutrientharmful_multi,
  macronutrient_spCondition,
} from '../configs/extraresult/macronutrientConfig';

function getMetaSource(reportMode) {
  if (reportMode === '9P') return bacteriaMetaConfig_9P;
  if (reportMode === 'Allergy and Skin') return bacteriaMetaConfig_allergy;
  if (reportMode === 'Allergy and Skin_kids') return bacteriaMetaConfig_allergy_kids;
  if (reportMode === 'Toxin') return bacteriaMetaConfig_toxin;
  if (reportMode === 'oms') return bacteriaMetaConfig_oms;
  if (reportMode === 'bwcallergy') return bacteriaMetaConfig_bwcallergy;
  if (reportMode === 'bwccore') return bacteriaMetaConfig_bwccore;
  if (reportMode === 'bwcdiamond') return bacteriaMetaConfig_bwcdiamond;
  return bacteriaMetaConfig;
}

export function computeMainScoresSync(formData, reportMode) {
  const newResults = {};
  const newScores = {};
  const metaSource = getMetaSource(reportMode);

  Object.entries(metaSource).forEach(([label, meta]) => {
    const rawValue = formData[label];
    // eslint-disable-next-line no-unused-vars
    const x = rawValue !== undefined && rawValue !== '' ? parseFloat(rawValue) : 0;
    if (!isNaN(x)) {
      try {
        // eslint-disable-next-line no-eval
        const calc = eval(meta.formula);
        newResults[label] = calc.toFixed(2);
        let matched;
        if (meta.customCondition) {
          // eslint-disable-next-line no-unused-vars
          const _universalBacteria = parseFloat(formData['Universal bacteria']) || 0;
          matched = meta.rangeMapping.find(
            r => typeof r.condition === 'function' && evaluateBacteriaCondition(r.condition, calc, formData, label)
          );
        } else {
          matched = meta.rangeMapping.find(r => calc >= r.min && calc <= r.max);
        }
        if (matched) newScores[label] = matched.score_cal;
      } catch (_) {
        newResults[label] = 'Invalid';
      }
    }
  });

  return { results: newResults, scores: newScores };
}

export function computeVitaminBScoresSync(formData, reportMode) {
  if (reportMode !== 'QPT') return { scores: {}, results: {} };

  const metaSource = bacteriaMetaConfig;
  const scores = {};
  const results = {};

  const getScoreCal = (speciesName, rawValue) => {
    const meta = metaSource[speciesName];
    if (!meta || !Array.isArray(meta.rangeMapping)) return 0;
    const raw = parseFloat(rawValue) || 0;
    let matched;
    if (meta.customCondition) {
      matched = meta.rangeMapping.find(
        r => typeof r.condition === 'function' && evaluateBacteriaCondition(r.condition, raw, formData, speciesName)
      );
    } else {
      matched = meta.rangeMapping.find(r => raw >= r.min && raw <= r.max);
    }
    return matched?.score_cal ?? 0;
  };

  const getMultiplier = (scoreCal) => {
    if (scoreCal === 3) return vitaminB_multi.case1.multiply;
    if (scoreCal === 2) return vitaminB_multi.case2.multiply;
    if (scoreCal === 1) return vitaminB_multi.case3.multiply;
    return vitaminB_multi.othercase.multiply;
  };

  Object.keys(vitaminB_sp).forEach((vitamin) => {
    const speciesMap = vitaminB_sp[vitamin];
    let sum = 0;
    Object.keys(speciesMap).forEach((speciesName) => {
      const rawValue = formData[speciesName];
      if (rawValue !== undefined && rawValue !== '') {
        const raw = parseFloat(rawValue) || 0;
        if (raw < 35) {
          sum += getMultiplier(getScoreCal(speciesName, raw)) * speciesMap[speciesName];
        }
      }
    });
    results[vitamin] = sum.toFixed(2);
    const config = vitaminB_spCondition[vitamin];
    if (config && Array.isArray(config.conditions)) {
      const matched = config.conditions.find(c => c.condition(sum));
      if (matched) scores[vitamin] = matched.score;
    }
  });

  return { scores, results };
}

export function computeNeurotransmitterScoresSync(formData, reportMode) {
  if (reportMode !== 'QPT') return { scores: {}, results: {} };

  const metaSource = bacteriaMetaConfig;
  const scores = {};
  const results = {};

  const getScoreCal = (speciesName, rawValue) => {
    const meta = metaSource[speciesName];
    if (!meta || !Array.isArray(meta.rangeMapping)) return 0;
    const raw = parseFloat(rawValue) || 0;
    let matched;
    if (meta.customCondition) {
      matched = meta.rangeMapping.find(
        r => typeof r.condition === 'function' && evaluateBacteriaCondition(r.condition, raw, formData, speciesName)
      );
    } else {
      matched = meta.rangeMapping.find(r => raw >= r.min && raw <= r.max);
    }
    return matched?.score_cal ?? 0;
  };

  const getMultiplier = (scoreCal) => {
    if (scoreCal === 5) return neurotransmitter_multi.case1.multiply;
    if (scoreCal === 4) return neurotransmitter_multi.case2.multiply;
    if (scoreCal === 3) return neurotransmitter_multi.case3.multiply;
    if (scoreCal === 2) return neurotransmitter_multi.case4.multiply;
    if (scoreCal === 1) return neurotransmitter_multi.case5.multiply;
    return neurotransmitter_multi.othercase.multiply;
  };

  Object.keys(neurotransmitter_sp).forEach((key) => {
    const speciesMap = neurotransmitter_sp[key];
    const name = key.replace('_sp', '');
    let sum = 0;
    Object.keys(speciesMap).forEach((speciesName) => {
      const rawValue = formData[speciesName];
      if (rawValue !== undefined && rawValue !== '') {
        const raw = parseFloat(rawValue) || 0;
        if (raw < 35) {
          sum += getMultiplier(getScoreCal(speciesName, raw)) * speciesMap[speciesName];
        }
      }
    });
    results[name] = sum.toFixed(2);
    const config = neurotransmitter_spCondition[name];
    if (config && Array.isArray(config.conditions)) {
      const matched = config.conditions.find(c => c.condition(sum));
      if (matched) scores[name] = matched.score;
    }
  });

  return { scores, results };
}

export function computeMetabolitesScoresSync(formData, reportMode) {
  if (reportMode !== 'QPT') return { scores: {}, results: {} };

  const metaSource = bacteriaMetaConfig;
  const scores = {};
  const results = {};

  const getScoreCal = (speciesName, rawValue) => {
    const meta = metaSource[speciesName];
    if (!meta || !Array.isArray(meta.rangeMapping)) return 0;
    const raw = parseFloat(rawValue) || 0;
    let matched;
    if (meta.customCondition) {
      matched = meta.rangeMapping.find(
        r => typeof r.condition === 'function' && evaluateBacteriaCondition(r.condition, raw, formData, speciesName)
      );
    } else {
      matched = meta.rangeMapping.find(r => raw >= r.min && raw <= r.max);
    }
    return matched?.score_cal ?? 0;
  };

  const getMultiplier = (scoreCal) => {
    if (scoreCal === 3) return metabolites_multi.case1.multiply;
    if (scoreCal === 2) return metabolites_multi.case2.multiply;
    if (scoreCal === 1) return metabolites_multi.case3.multiply;
    return metabolites_multi.othercase.multiply;
  };

  Object.keys(metabolites_sp).forEach((key) => {
    const speciesMap = metabolites_sp[key];
    const name = key.replace('_sp', '');
    let sum = 0;
    Object.keys(speciesMap).forEach((speciesName) => {
      const rawValue = formData[speciesName];
      if (rawValue !== undefined && rawValue !== '') {
        const raw = parseFloat(rawValue) || 0;
        if (raw < 35) {
          sum += getMultiplier(getScoreCal(speciesName, raw)) * speciesMap[speciesName];
        }
      }
    });
    results[name] = sum.toFixed(2);
    const config = metabolites_spCondition[name];
    if (config && Array.isArray(config.conditions)) {
      const matched = config.conditions.find(c => c.condition(sum));
      if (matched) scores[name] = matched.score;
    }
  });

  return { scores, results };
}

export function computeMacronutrientScoresSync(formData, reportMode) {
  if (reportMode !== 'QPT') return { scores: {}, results: {} };

  const metaSource = bacteriaMetaConfig;
  const scores = {};
  const results = {};

  const getScoreCal = (speciesName, rawValue) => {
    const meta = metaSource[speciesName];
    if (!meta || !Array.isArray(meta.rangeMapping)) return 0;
    const raw = parseFloat(rawValue) || 0;
    let matched;
    if (meta.customCondition) {
      matched = meta.rangeMapping.find(
        r => typeof r.condition === 'function' && evaluateBacteriaCondition(r.condition, raw, formData, speciesName)
      );
    } else {
      matched = meta.rangeMapping.find(r => raw >= r.min && raw <= r.max);
    }
    return matched?.score_cal ?? 0;
  };

  const getMultiplier = (scoreCal, isHarmful) => {
    if (isHarmful) {
      if (scoreCal === 5) return macronutrientharmful_multi.case1.multiply;
      if (scoreCal === 4) return macronutrientharmful_multi.case2.multiply;
      if (scoreCal === 3) return macronutrientharmful_multi.case3.multiply;
      return macronutrientharmful_multi.othercase.multiply;
    }
    if (scoreCal === 1) return macronutrient_multi.case1.multiply;
    if (scoreCal === 2) return macronutrient_multi.case2.multiply;
    if (scoreCal === 3) return macronutrient_multi.case3.multiply;
    return macronutrient_multi.othercase.multiply;
  };

  Object.keys(macronutrient_sp).forEach((key) => {
    const speciesMap = macronutrient_sp[key];
    let sum = 0;
    Object.keys(speciesMap).forEach((speciesName) => {
      const rawValue = formData[speciesName];
      if (rawValue !== undefined && rawValue !== '') {
        const raw = parseFloat(rawValue) || 0;
        if (raw < 35) {
          const isHarmful = macronutrient_harmfulsp.includes(speciesName);
          sum += getMultiplier(getScoreCal(speciesName, raw), isHarmful) * speciesMap[speciesName];
        }
      }
    });
    results[key] = sum.toFixed(2);
    const config = macronutrient_spCondition[key];
    if (config && Array.isArray(config.conditions)) {
      const matched = config.conditions.find(c => c.condition(sum));
      if (matched) scores[key] = matched.score;
    }
  });

  return { scores, results };
}
